import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, ButtonBase, CircularProgress, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { ReactComponent as PdfIcon } from "Components/_Shared/Icons/Assets/pdf.svg";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type ReactNode, useState } from "react";
import { QuestionnairePdfEmailConfirmModal } from "./QuestionnairePdfEmailConfirmModal";
import { type QuestionnairePdfType, useQuestionnairePdf } from "./useQuestionnairePdf";

type DocumentActionProps = {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

const DocumentAction = ({ icon, label, onClick, disabled }: DocumentActionProps) => (
  <ButtonBase
    disabled={disabled}
    onClick={onClick}
    sx={{
      width: "100%",
      height: "100%",
      gap: Spacing.M,
      py: Spacing.L,
      color: "text.primary",
      "&:hover": { bgcolor: "grey.50" },
      "&.Mui-disabled": { color: "text.disabled" },
    }}
  >
    {icon}
    <Typography variant="body2" fontWeight={500} color="inherit">
      {label}
    </Typography>
  </ButtonBase>
);

type Props = {
  isEvaluated: boolean;
};

export const QuestionnairePdfActions = ({ isEvaluated }: Props) => {
  const { t } = useResource();
  const [confirmType, setConfirmType] = useState<QuestionnairePdfType | null>(null);
  const { clientEmail, isSending, sendPdf, openPreview } = useQuestionnairePdf();

  const sendAction = (
    <DocumentAction
      icon={isSending.filled ? <CircularProgress size={16} /> : <EmailOutlinedIcon fontSize="small" />}
      label={t(Resources.Questionnaire.Evaluation.SendPdf.Send)}
      disabled={!clientEmail || isSending.filled}
      onClick={() => setConfirmType("filled")}
    />
  );

  return (
    <>
      <Stack spacing={Spacing.L}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t(Resources.Questionnaire.Evaluation.SendPdf.Title)}
        </Typography>
        <Box bgcolor="white" border="1px solid" borderColor="divider" borderRadius={Radius.L} overflow="hidden">
          <Flex alignItems="center" gap={Spacing.L} p={Spacing.L}>
            <Flex
              alignItems="center"
              justifyContent="center"
              width={40}
              height={40}
              flexShrink={0}
              borderRadius={Radius.M}
              bgcolor="primary.light"
              color="primary.main"
            >
              <PdfIcon />
            </Flex>
            <Stack minWidth={0}>
              <Typography variant="body2" fontWeight={600}>
                {t(isEvaluated ? Resources.Questionnaire.Evaluation.SendPdf.Evaluated : Resources.Questionnaire.Evaluation.SendPdf.Filled)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(Resources.Questionnaire.Evaluation.SendPdf.Format)}
              </Typography>
            </Stack>
          </Flex>
          <Flex borderTop="1px solid" borderColor="divider">
            <Box flex={1}>
              <DocumentAction
                icon={<VisibilityOutlinedIcon fontSize="small" />}
                label={t(Resources.Questionnaire.Evaluation.SendPdf.Preview)}
                onClick={() => openPreview("filled")}
              />
            </Box>
            <Divider orientation="vertical" flexItem />
            {clientEmail ? (
              <Box flex={1}>{sendAction}</Box>
            ) : (
              <Tooltip title={t(Resources.Questionnaire.Evaluation.SendPdf.EmailInvalid)}>
                <Box flex={1} sx={{ cursor: "not-allowed" }}>
                  {sendAction}
                </Box>
              </Tooltip>
            )}
          </Flex>
        </Box>
      </Stack>

      <QuestionnairePdfEmailConfirmModal
        type={confirmType}
        clientEmail={clientEmail}
        onClose={() => setConfirmType(null)}
        onConfirm={sendPdf}
      />
    </>
  );
};
